import { useEffect, useState } from 'react';
import axios from 'axios';
import { HourBars } from './DropHourBars';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
const rate = (opens, delivered) => (delivered ? `${Math.min(100, Math.round((opens / delivered) * 100))}%` : '—');
const nice = (label) => label.replaceAll('{product}', '[product]');

function PushRow({ p }) {
  return (
    <li className="p-2 space-y-0.5" data-testid={`drop-stat-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-white text-[12px] font-bold truncate">{p.title}</p>
        <span className="text-[#39FF14] text-[12px] font-bold shrink-0" data-testid={`drop-stat-rate-${p.id}`}>{rate(p.opens, p.delivered)}</span>
      </div>
      <p className="text-[#A3C7A8] text-[11px] line-clamp-2">{p.body}</p>
      <p className="text-[#6B8E73] text-[10px]">
        {fmt(p.sent_at)} · {p.product_name} · {p.delivered ?? '…'} delivered · {p.opens} opened
        {p.saved_label && <span className="ml-1 text-[#FFD700] border border-[#FFD700]/50 px-1">★ {nice(p.saved_label)}</span>}
      </p>
    </li>
  );
}

export function DropPushStats({ region }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/api/drop/push-stats/${region}`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ items: [], by_wording: [] }));
  }, [region]);
  if (!data) return <p className="font-mono text-xs text-[#6B8E73]">Loading…</p>;
  if (!data.items.length) return <p className="font-mono text-xs text-[#6B8E73]" data-testid="drop-stats-empty">// No Drop pushes sent in {region.toUpperCase()} yet.</p>;
  return (
    <div className="space-y-3 font-mono" data-testid={`drop-stats-${region}`}>
      <div>
        <p className="text-[#FF9F0A] uppercase text-[11px] mb-1">By wording</p>
        <ul className="border border-[#2A0F3D] divide-y divide-[#2A0F3D]">
          {data.by_wording.map((w) => (
            <li key={w.label} className="flex items-center justify-between gap-2 px-2 py-1 text-[11px] text-[#A3C7A8]" data-testid={`drop-stat-wording-${w.label}`}>
              <span className="truncate">{w.label === 'Custom wording' ? w.label : `★ ${nice(w.label)}`}</span>
              <span className="shrink-0">{w.pushes} sent · {w.opens}/{w.delivered} · <b className="text-[#39FF14]">{rate(w.opens, w.delivered)}</b></span>
            </li>
          ))}
        </ul>
      </div>
      <HourBars title="Open % by hour sent (ET)" testId="drop-hours-sent" values={data.by_send_hour.map((b) => (b.delivered ? Math.min(100, Math.round((b.opens / b.delivered) * 100)) : 0))}
        hint={(h) => { const b = data.by_send_hour[h]; return `${b.pushes} sent · ${b.opens}/${b.delivered} opened`; }} suffix="%"
        eligible={(h) => data.by_send_hour[h].delivered >= 20} />
      <p className="text-[10px] text-[#6B8E73] -mt-2">// Grey bars = under 20 delivered, not counted for "Best".</p>
      <HourBars title="Opens by hour tapped (ET)" testId="drop-hours-tapped" values={data.opens_by_hour} hint={(h) => `${data.opens_by_hour[h]} opens`} />
      <div>
        <p className="text-[#FF9F0A] uppercase text-[11px] mb-1">Recent pushes</p>
        <ul className="border border-[#2A0F3D] divide-y divide-[#2A0F3D] max-h-72 overflow-y-auto">
          {data.items.map((p) => <PushRow key={p.id} p={p} />)}
        </ul>
      </div>
    </div>
  );
}
